import React from 'react';
import {
    MessageSquare,
    X,
    Loader2,
} from 'lucide-react';

const interventionTypes = [
    { value: 'annulation_client', label: 'Annulation client' },
    { value: 'demande_documents', label: 'Demande de documents' },
    { value: 'informations_manquantes', label: 'Informations manquantes' },
    { value: 'autre', label: 'Autre' }
];

export interface WorkflowInterventionModalProps {
    showInterventionModal: boolean;
    interventionType: string;
    interventionMessage: string;
    interventionIsPending: boolean;
    onClose: () => void;
    onInterventionTypeChange: (value: string) => void;
    onInterventionMessageChange: (value: string) => void;
    onCreateIntervention: () => void;
}

const WorkflowInterventionModal: React.FC<WorkflowInterventionModalProps> = ({
    showInterventionModal,
    interventionType,
    interventionMessage,
    interventionIsPending,
    onClose,
    onInterventionTypeChange,
    onInterventionMessageChange,
    onCreateIntervention,
}) => {
    if (!showInterventionModal) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                                <MessageSquare className="w-5 h-5 text-gray-700" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Créer une intervention</h3>
                                <p className="text-xs text-gray-500 mt-0.5">Demander l'aide d'un conseiller</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="px-6 py-5 space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Type d'intervention
                        </label>
                        <select
                            value={interventionType}
                            onChange={(e) => onInterventionTypeChange(e.target.value)}
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                        >
                            <option value="">Sélectionner un type</option>
                            {interventionTypes.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Message
                        </label>
                        <textarea
                            value={interventionMessage}
                            onChange={(e) => onInterventionMessageChange(e.target.value)}
                            rows={5}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors resize-none"
                            placeholder="Décrire l'intervention nécessaire..."
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-end space-x-3">
                    <button
                        onClick={() => {
                            onClose();
                            onInterventionTypeChange('');
                            onInterventionMessageChange('');
                        }}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={onCreateIntervention}
                        disabled={interventionIsPending}
                        className="px-5 py-2.5 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm transition-colors"
                    >
                        {interventionIsPending ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Création...
                            </>
                        ) : (
                            <>
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Créer l'intervention
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WorkflowInterventionModal;
